import { ColDef, ValueGetterParams } from '@ag-grid-community/core'
import { NgGridComponents } from '@northfork/ag-grid-angular'
import { SourceDefinition } from '../models/source-definition.model'
import {
  MATCH_COUNTS_FIELD,
  MatchTargetKey,
  TARGET_META,
} from '../models/source-key.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import { MatchingService } from '../services/matching.service'
import {
  commentsCellRenderer,
  makeMatchCountRenderer,
  sourceCellRenderer,
  statusCellRenderer,
} from '../grid/renderers/renderers'

const CENTER = 'wb-center-cell'

/**
 * The filter for a match-count column ONLY when it is riding on the catalog's own field — i.e. only
 * from {@link heliosColumnOverride}, never from {@link matchCountColumn} below. The two builders key
 * fields differently, and that difference is exactly what decides whether a real filter belongs here:
 *
 *  - `heliosColumnOverride` decorates a CATALOG column (`custodyMatches` / `tradingMatches` /
 *    `enfusionMatches`, MATCH_COUNTS storage) that survived into the ColDefs precisely because the
 *    catalog returned it — so it IS in `fieldMeta()`'s map, and `NgGridComponents.numberFilter` (not
 *    ag-Grid's native `agNumberColumnFilter`, which the house converters and the Filters modal's
 *    `getFilterType` cannot route on) is a real, working filter.
 *  - `matchCountColumn` builds the UI-owned `matchCount:${target}` colId, which only survives into the
 *    final ColDefs when the catalog did NOT return the aliased field (see `col-def-factory.ts`'s
 *    `aliased` dedup) — by construction it is therefore NEVER in `fieldMeta()`, for ANY target, not
 *    only `cw`. A working-looking filter there is worse than an absent one: the Filters modal would
 *    offer a live Number filter over a predicate `buildQueryRequest` silently drops before /query.
 *
 * `cw` never reaches this function at all (see `matchCountColumn`'s own `filter: false`): Clearwater
 * match counts are computed client-side from the cross-grid match graph and have no backend field on
 * ANY source, catalog-provided or not.
 */
const matchCountFilter = (target: MatchTargetKey): ColDef['filter'] =>
  target === 'cw' ? false : NgGridComponents.numberFilter

const sourceValue = (row: WorkbenchRow | undefined): string => {
  if (!row) return ''
  if (row.origin === 'api') return row.sourceLabel ?? ''
  if (row.origin === 'cust') return 'Custody'
  if (row.origin === 'trad') return 'Trading'
  return ''
}

/**
 * Catalog `field` -> the computed colId it supersedes.
 *
 * The API returns its own HELIOS group covering the same ground the UI builds itself. Presence,
 * order, label and default visibility come from the catalog; the renderer and value accessor stay
 * UI-owned, because the backend deliberately returns neither. This map is what stops the two
 * producing duplicate columns — note the match counts do not even collide by id (the API names
 * them after the source being matched, the UI keys them by target), so they need the alias.
 *
 * KEYS ARE THE POST-PVAL-26116 FIELD NAMES. Two were renamed by that contract — `status` ->
 * `matchStatus` and `source` -> `recordSource` — and a key that does not match the catalog's own
 * spelling silently stops aliasing, which shows up as the catalog column AND the computed column both
 * appearing. (`securityId` -> `securityID` and `normalizationModelId` ->
 * `positionNormalizationModelId` were renamed too but are ordinary data columns with no computed
 * counterpart, so they never appear here.)
 *
 * `statusChangedBy`, `comments` and `clearwaterMatches` were DROPPED from the catalog by the same
 * contract, so their entries can never match a returned field again. They are left listed rather than
 * deleted because the computed columns they alias still exist — deleting the alias without deleting
 * the column is the change that would make a duplicate appear if a backend ever returned the field
 * again. Retiring those computed columns themselves is tracked separately.
 *
 * `breaks` was dropped by the same contract AND its computed column has now been retired, so both
 * halves went together and there is no alias left to keep: nothing exists for a returned `breaks`
 * field to duplicate, and it would render as an ordinary data column.
 */
export const COMPUTED_COLUMN_ALIASES: Record<string, string> = {
  recordSource: 'source',
  matchStatus: 'status',
  accountId: 'accountId',
  comments: 'comments',
  statusChangedBy: 'statusChangedBy',
  dataSourceName: 'dataSourceName',
  custodyMatches: 'matchCount:cust',
  tradingMatches: 'matchCount:trad',
  enfusionMatches: 'matchCount:api',
  clearwaterMatches: 'matchCount:cw',
}

/** Catalog `field` -> the match target its count refers to (the inverse of `MATCH_COUNTS_FIELD` —
 *  the catalog and the row's own `matchCounts` blob happen to share the same field names). */
const MATCH_FIELD_TARGETS: Record<string, MatchTargetKey> = Object.fromEntries(
  (Object.entries(MATCH_COUNTS_FIELD) as [MatchTargetKey, string][]).map(
    ([target, field]) => [field, target],
  ),
)

/**
 * Rendering behaviour for a catalog column the UI owns the presentation of, or `undefined` for an
 * ordinary data column. Merged over the type-driven ColDef so the catalog still supplies colId,
 * header and visibility.
 *
 * Positions right-aligns NUM by default (col-def-factory RIGHT), so the match counts — which are NUM
 * but centred — must actively CLEAR the type-driven alignment / formatter / comparator
 * / filterValueGetter (contract Decision 5).
 */
export function heliosColumnOverride(
  field: string,
  matching: MatchingService,
): Partial<ColDef> | undefined {
  const target = MATCH_FIELD_TARGETS[field]
  if (target) {
    return {
      width: 120,
      sortable: true,
      filter: matchCountFilter(target),
      cellClass: CENTER,
      headerClass: CENTER,
      cellRenderer: makeMatchCountRenderer(TARGET_META[target].color),
      valueGetter: (params: ValueGetterParams) =>
        params.data ? matching.matchCount(params.data, target) : 0,
      type: undefined,
      valueFormatter: undefined,
      comparator: undefined,
      filterValueGetter: undefined,
    }
  }

  switch (field) {
    case 'source':
      return {
        width: 120,
        filter: 'agSetColumnFilter',
        cellRenderer: sourceCellRenderer,
        valueGetter: (params: ValueGetterParams) => sourceValue(params.data),
      }
    case 'status':
      return {
        width: 130,
        filter: 'agSetColumnFilter',
        cellRenderer: statusCellRenderer,
        valueGetter: (params: ValueGetterParams) =>
          params.data ? matching.statusOf(params.data) : '',
      }
    case 'comments':
      return {
        width: 96,
        sortable: true,
        filter: false,
        cellClass: CENTER,
        headerClass: CENTER,
        cellRenderer: commentsCellRenderer,
        valueGetter: (params: ValueGetterParams) => params.data?.comments ?? 0,
      }
    default:
      return undefined
  }
}

// ---- computed (UI-owned) columns, used as the synchronous seed and as the "survivors" pool ----

const sourceColumn = (): ColDef => ({
  colId: 'source',
  field: 'source',
  headerName: 'Source',
  width: 120,
  filter: 'agSetColumnFilter',
  cellRenderer: sourceCellRenderer,
  valueGetter: (params: ValueGetterParams) => sourceValue(params.data),
})

const statusColumn = (matching: MatchingService): ColDef => ({
  colId: 'status',
  field: 'status',
  headerName: 'Status',
  width: 130,
  filter: 'agSetColumnFilter',
  cellRenderer: statusCellRenderer,
  valueGetter: (params: ValueGetterParams) =>
    params.data ? matching.statusOf(params.data) : '',
})

const matchCountColumn = (
  source: SourceDefinition,
  matching: MatchingService,
): ColDef[] =>
  source.matchTargets.map(target => ({
    colId: `matchCount:${target}`,
    field: `matchCount:${target}`,
    headerName: `${TARGET_META[target].label} Matches`,
    width: 120,
    sortable: true,
    // Never filterable here, for ANY target: this is the UI-owned colId, which by construction only
    // survives into the ColDefs when the catalog did NOT return the aliased field — see
    // matchCountFilter's doc comment above. A catalog-provided match count is filterable through
    // heliosColumnOverride instead, on the catalog's own field.
    filter: false,
    cellClass: CENTER,
    headerClass: CENTER,
    cellRenderer: makeMatchCountRenderer(TARGET_META[target].color),
    valueGetter: (params: ValueGetterParams) =>
      params.data ? matching.matchCount(params.data, target) : 0,
  }))

const textColumn = (
  colId: string,
  headerName: string,
  width: number,
  read: (row: WorkbenchRow) => string,
): ColDef => ({
  colId,
  field: colId,
  headerName,
  width,
  // Not filterable: this ColDef is only ever used as the pre-catalog-resolve seed or as a survivor
  // for a field the catalog no longer sends (`statusChangedBy`, per the PVAL-26116 drop list above) —
  // in either case there is no catalog entry for `fieldMeta()` to resolve, so `buildQueryRequest`
  // drops any predicate on it before it reaches /query. A `agSetColumnFilter` here (as before) would
  // build its option list from whatever rows happen to be loaded, presenting the first block's values
  // as the column's whole domain, AND commit the user to a filter that silently filters nothing —
  // exactly the failure mode `col-def-factory.ts`'s closed-domain rule exists to prevent. When the
  // catalog DOES send this field, it supersedes this fallback with its own correctly-typed ColDef, so
  // this never governs the resolved-catalog case.
  filter: false,
  valueGetter: (params: ValueGetterParams) =>
    params.data ? read(params.data) : '',
})

const commentsColumn = (): ColDef => ({
  colId: 'comments',
  field: 'comments',
  headerName: 'Comments',
  width: 96,
  sortable: true,
  filter: false,
  cellClass: CENTER,
  headerClass: CENTER,
  cellRenderer: commentsCellRenderer,
  valueGetter: (params: ValueGetterParams) => params.data?.comments ?? 0,
})

/**
 * The leading, fixed Helios columns for a source. Used as the synchronous seed (before the catalog
 * resolves) and as the pool of survivors spliced in after the catalog's HELIOS block once it does.
 * Which columns a source gets is driven by capability flags on the {@link SourceDefinition}, not
 * hardcoded source-key checks.
 */
export function buildComputedColumns(
  source: SourceDefinition,
  matching: MatchingService,
): ColDef[] {
  const columns: ColDef[] = []

  if (source.hasSourceColumn) columns.push(sourceColumn())

  columns.push(statusColumn(matching))
  columns.push(...matchCountColumn(source, matching))

  if (source.hasSubSource) {
    columns.push(
      textColumn(
        'statusChangedBy',
        'Status Changed By',
        150,
        row => row.statusChangedBy ?? '',
      ),
      textColumn(
        'dataSourceName',
        'Data Source Name',
        150,
        row => row.dataSourceName ?? '',
      ),
    )
  }

  if (source.hasLeadingAccountId) {
    columns.push(
      textColumn('accountId', 'Account ID', 104, row => row.accountId),
    )
  }

  if (source.hasComments) columns.push(commentsColumn())

  return columns
}
